/**
 * A/B-Tests fuer Landing Pages — Zuweisung im Browser.
 *
 * Warum nicht serverseitig: die Startseite wird vorgerendert und mit
 * `s-maxage=300` ueber Cloudflare ausgeliefert. Wuerde der Server die Variante
 * waehlen, muesste die Antwort privat werden — das CDN-Caching der
 * meistbesuchten Seite faellt damit weg.
 *
 * Die Zuweisung ist deshalb rein rechnerisch: aus Besucher-ID und Test-Schluessel
 * wird ein Hash gebildet und daraus der Arm bestimmt. Derselbe Besucher landet
 * bei jedem Aufruf im selben Arm, ohne dass die Zuweisung irgendwo
 * nachgeschlagen werden muss — auch dann, wenn die Konfiguration in einem Tab
 * schon neu geladen ist und im anderen noch nicht.
 */
import { useEffect, useState } from 'react';
import { API_BASE_URL } from '../services/db';

export const VISITOR_COOKIE = 'tc_vid';
export const EXPERIMENT_CACHE_KEY = 'tc_experiment_cache';
export const EXPERIMENT_PREVIEW_KEY = 'tc_experiment_preview';
export const EXPERIMENT_PREVIEW_PARAM = 'tc_exp';

/** Muss mit LANDING_COMPONENTS in backend/routers/experiments.py uebereinstimmen. */
export type LandingComponentId =
  | 'landing-redesign'
  | 'landing-optimized'
  | 'landing-v3'
  | 'landing-v4';

export interface ExperimentVariant {
  id: string;
  component: LandingComponentId;
  weight: number;
}

export interface ActiveExperiment {
  id: string;
  key: string;
  surface: string;
  variants: ExperimentVariant[];
}

/** Antwort von /experiments/active — Test UND Standardseite der Oberflaeche. */
export interface SurfaceConfig {
  experiment: ActiveExperiment | null;
  /** Ueberschreibt die im Code hinterlegte Standardseite. Null = Code-Standard. */
  defaultComponent: LandingComponentId | null;
}

export interface VariantAssignment {
  /** Null, wenn kein Test laeuft und nur die Standardseite greift. */
  experimentKey: string | null;
  variantId: string | null;
  component: LandingComponentId;
  isPreview: boolean;
}

const CACHE_TTL_MS = 5 * 60 * 1000;

/* ─── Besucher-ID ───────────────────────────────────────────────────────────
 *
 * Eine zufaellige UUID, sonst nichts. Kein Personenbezug, keine Verkettung mit
 * Konto oder IP — sie dient allein dazu, denselben Besucher bei zwei Aufrufen
 * derselben Variante zuzuordnen.
 */

export const readCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

const randomId = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID().replace(/-/g, '');
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
};

export const getVisitorId = (): string | null => {
  if (typeof document === 'undefined') return null;
  const existing = readCookie(VISITOR_COOKIE);
  if (existing) return existing;

  const id = randomId();
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  // Ein Jahr, damit ein Test ueber Wochen denselben Besucher wiedererkennt.
  document.cookie = `${VISITOR_COOKIE}=${id}; path=/; max-age=31536000; SameSite=Lax${secure}`;
  return id;
};

/* ─── Entscheidung des Servers ──────────────────────────────────────────────
 *
 * Das Backend waehlt die Variante bereits beim Ausliefern und legt sie als
 * `window.__TC_ASSIGNMENT__` ins Dokument. Wird sie hier uebernommen, rendert
 * React genau das, was schon auf dem Bildschirm steht — ohne diesen Schritt
 * wuerde neu zugewiesen und die Seite doch noch umgebaut.
 *
 * `__TC_FORCE_LANDING__` setzt nur das Vorrender-Werkzeug, um jede Variante
 * einmal als HTML zu erzeugen. Es kommt in keiner ausgelieferten Seite vor.
 */

declare global {
  interface Window {
    __TC_ASSIGNMENT__?: { experimentKey: string; variantId: string; component: string };
    __TC_FORCE_LANDING__?: string;
  }
}

export const readServerAssignment = (): VariantAssignment | null => {
  if (typeof window === 'undefined') return null;

  const forced = window.__TC_FORCE_LANDING__;
  if (forced) {
    return { experimentKey: null, variantId: null, component: forced as LandingComponentId, isPreview: true };
  }

  const server = window.__TC_ASSIGNMENT__;
  if (!server?.component) return null;
  return {
    experimentKey: server.experimentKey ?? null,
    variantId: server.variantId ?? null,
    component: server.component as LandingComponentId,
    isPreview: false,
  };
};

/* ─── Zuweisung ─────────────────────────────────────────────────────────── */

/** FNV-1a. Klein, deterministisch, ohne Abhaengigkeit — mehr braucht es nicht. */
const hashToUnit = (input: string): number => {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0) / 0xffffffff;
};

export const assignVariant = (
  experiment: ActiveExperiment,
  visitorId: string,
): ExperimentVariant | null => {
  const variants = experiment.variants.filter(v => v.weight > 0);
  if (variants.length === 0) return null;

  const totalWeight = variants.reduce((sum, v) => sum + v.weight, 0);
  if (totalWeight <= 0) return null;

  // Der Test-Schluessel geht in den Hash ein, damit ein Besucher nicht in jedem
  // Test in derselben Position landet. Sonst korrelieren aufeinanderfolgende
  // Tests miteinander und ein Ausreisser-Segment verzerrt sie alle gleich.
  const point = hashToUnit(`${visitorId}:${experiment.key}`) * totalWeight;

  let cumulative = 0;
  for (const variant of variants) {
    cumulative += variant.weight;
    if (point < cumulative) return variant;
  }
  return variants[variants.length - 1];
};

/* ─── Vorschau ──────────────────────────────────────────────────────────────
 *
 * `?tc_exp=b` erzwingt einen Arm. Solche Aufrufe werden als Vorschau markiert
 * und aus der Auswertung herausgehalten — sonst faelscht jeder eigene
 * Kontrollblick das Ergebnis des Arms, den man sich gerade ansieht.
 */

export const readPreviewOverride = (): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    const fromQuery = new URLSearchParams(window.location.search).get(EXPERIMENT_PREVIEW_PARAM);
    if (fromQuery) {
      window.sessionStorage.setItem(EXPERIMENT_PREVIEW_KEY, fromQuery);
      return fromQuery;
    }
    return window.sessionStorage.getItem(EXPERIMENT_PREVIEW_KEY);
  } catch {
    return null;
  }
};

export const clearPreviewOverride = (): void => {
  try {
    window.sessionStorage.removeItem(EXPERIMENT_PREVIEW_KEY);
  } catch {
    /* Speicher kann im privaten Modus gesperrt sein. */
  }
};

/* ─── Konfiguration laden ───────────────────────────────────────────────────
 *
 * Der zwischengespeicherte Stand wird sofort verwendet, der frische danach
 * nachgezogen. Beim allerersten Besuch gibt es keinen Zwischenspeicher — dann
 * rendert kurz die Kontrollvariante, bis die Konfiguration da ist. Ab dem
 * zweiten Aufruf ist die Entscheidung sofort da und die Seite bleibt ruhig.
 */

interface CachedConfig {
  fetchedAt: number;
  experiment: ActiveExperiment | null;
  defaultComponent?: LandingComponentId | null;
}

const readCache = (surface: string): CachedConfig | null => {
  try {
    const raw = window.localStorage.getItem(`${EXPERIMENT_CACHE_KEY}:${surface}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedConfig;
    if (typeof parsed?.fetchedAt !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeCache = (surface: string, config: SurfaceConfig): void => {
  try {
    window.localStorage.setItem(
      `${EXPERIMENT_CACHE_KEY}:${surface}`,
      JSON.stringify({
        fetchedAt: Date.now(),
        experiment: config.experiment,
        defaultComponent: config.defaultComponent,
      }),
    );
  } catch {
    /* Speicher kann im privaten Modus gesperrt sein. */
  }
};

export const fetchSurfaceConfig = async (surface: string): Promise<SurfaceConfig> => {
  const response = await fetch(`${API_BASE_URL}/experiments/active?surface=${encodeURIComponent(surface)}`);
  if (!response.ok) throw new Error(`experiments/active: ${response.status}`);
  const data = await response.json();
  return {
    experiment: (data?.experiment as ActiveExperiment | null) ?? null,
    defaultComponent: (data?.default_component as LandingComponentId | null) ?? null,
  };
};

const recordExposure = (
  experiment: ActiveExperiment,
  variantId: string,
  visitorId: string,
  isPreview: boolean,
): void => {
  // Der Aufruf darf das Rendern nicht aufhalten und darf nichts kaputt machen,
  // wenn er scheitert: eine verlorene Zaehlung ist harmlos, eine kaputte
  // Startseite nicht.
  try {
    const body = JSON.stringify({
      experiment_key: experiment.key,
      variant: variantId,
      visitor_id: visitorId,
      path: window.location.pathname,
      referrer: document.referrer || null,
      is_preview: isPreview,
    });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(
        `${API_BASE_URL}/experiments/expose`,
        new Blob([body], { type: 'application/json' }),
      );
      return;
    }
    void fetch(`${API_BASE_URL}/experiments/expose`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    /* Zaehlung ist Beiwerk. */
  }
};

/** Meldet ein erreichtes Ziel. Anmeldungen verbucht das Backend selbst. */
export const trackExperimentConversion = (metric: string, valueCents = 0): void => {
  const visitorId = readCookie(VISITOR_COOKIE);
  if (!visitorId) return;
  try {
    void fetch(`${API_BASE_URL}/experiments/convert`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ visitor_id: visitorId, metric, value_cents: valueCents }),
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    /* siehe oben */
  }
};

const resolve = (
  config: SurfaceConfig | null,
  visitorId: string | null,
): VariantAssignment | null => {
  const fallback: VariantAssignment | null = config?.defaultComponent
    ? {
      experimentKey: null,
      variantId: null,
      component: config.defaultComponent,
      isPreview: false,
    }
    : null;

  const experiment = config?.experiment ?? null;
  // Ohne laufenden Test greift die im Admin gesetzte Standardseite. Sie ist
  // der Grund, warum „Gewinner ausrollen" ohne Deploy funktioniert.
  if (!experiment || !visitorId) return fallback;

  const override = readPreviewOverride();
  if (override) {
    const forced = experiment.variants.find(v => v.id === override);
    if (forced) {
      return {
        experimentKey: experiment.key,
        variantId: forced.id,
        component: forced.component,
        isPreview: true,
      };
    }
  }

  const variant = assignVariant(experiment, visitorId);
  if (!variant) return fallback;
  return {
    experimentKey: experiment.key,
    variantId: variant.id,
    component: variant.component,
    isPreview: false,
  };
};

/**
 * Liefert die Variante fuer eine Oberflaeche, oder null (= Standardseite).
 *
 * Gezaehlt wird genau einmal je Seitenaufruf, auch wenn React die Komponente
 * mehrfach rendert.
 */
export const useExperimentVariant = (surface: string): VariantAssignment | null => {
  const [assignment, setAssignment] = useState<VariantAssignment | null>(() => {
    if (typeof window === 'undefined') return null;
    // Die Entscheidung des Servers hat Vorrang: sie steht bereits im HTML, das
    // der Besucher sieht. Alles andere wuerde die Seite umbauen.
    const fromServer = readServerAssignment();
    if (fromServer) return fromServer;

    const cached = readCache(surface);
    if (!cached) return null;
    return resolve(
      { experiment: cached.experiment, defaultComponent: cached.defaultComponent ?? null },
      readCookie(VISITOR_COOKIE),
    );
  });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    let cancelled = false;

    const cached = readCache(surface);
    const isFresh = cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS;

    const serverAssignment = readServerAssignment();

    const apply = (config: SurfaceConfig) => {
      if (cancelled) return;
      // Eine Besucher-ID wird nur gesetzt, wenn tatsaechlich ein Test laeuft.
      // Ohne Test gibt es nichts zu messen und damit keinen Grund, ein Cookie
      // zu schreiben.
      const visitorId = config.experiment ? getVisitorId() : readCookie(VISITOR_COOKIE);
      const computed = resolve(config, visitorId);

      /*
       * Hat der Server entschieden, bleibt es dabei — auch wenn die eigene
       * Rechnung etwas anderes ergaebe. Ein Wechsel waere hier das Schlimmste
       * von beidem: der Besucher saehe erst die eine, dann die andere Variante,
       * und gezaehlt wuerde eine dritte Wahrheit.
       *
       * Weichen beide ab, ist das ein Fehler und gehoert gemeldet, nicht
       * stillschweigend ueberdeckt.
       */
      const next = serverAssignment ?? computed;
      if (serverAssignment && computed && computed.component !== serverAssignment.component) {
        console.warn(
          '[tc-experiments] Server und Browser weisen unterschiedlich zu:',
          { server: serverAssignment.component, browser: computed.component },
        );
      }

      setAssignment(next);
      if (next?.variantId && config.experiment && visitorId) {
        recordExposure(config.experiment, next.variantId, visitorId, next.isPreview);
      }
    };

    const cachedConfig = (c: CachedConfig): SurfaceConfig => ({
      experiment: c.experiment,
      defaultComponent: c.defaultComponent ?? null,
    });

    if (isFresh) {
      apply(cachedConfig(cached!));
      return () => { cancelled = true; };
    }

    fetchSurfaceConfig(surface)
      .then(config => {
        writeCache(surface, config);
        apply(config);
      })
      .catch(() => {
        // Netzfehler darf die Seite nicht leeren: der zwischengespeicherte
        // Stand gilt weiter, sonst die im Code hinterlegte Standardseite.
        if (cached) apply(cachedConfig(cached));
      });

    return () => { cancelled = true; };
  }, [surface]);

  return assignment;
};
